import React, {useState, useMemo} from 'react';
import _ from 'lodash';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {ListItemText, Menu, MenuItem, Select, Skeleton, Stack, styled, Typography} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {useSnackbar} from 'notistack';
import api from '../util/api.js';
import {formatDate, formatDateIsrael} from '../util/util.js';
import {useBusyCallback} from '../util/react_hooks.js';
import SCButton from './SCButton.js';

const OrderType = {
    MONTH: 'חודשי',
    YEAR: 'שנתי',
};

const useOrders = userId=>useQuery({
    queryKey: ['tarotmail_orders', userId],
    queryFn: ()=>api.admin.tarotmailOrders.getUserOrders(userId),
});

const OrderListSkeleton = ()=><Stack direction="column" gap={2}>
  <Skeleton variant="rounded" width="100%" height={50}/>
  <Skeleton variant="rounded" width="100%" height={50}/>
  <Skeleton variant="rounded" width="100%" height={50}/>
</Stack>;

const OrderListEmpty = ()=>{
    return 'עדיין אין מנוי למשתמש הזה';
};

const OrderListItemContainer = styled(Stack, {shouldForwardProp: p=>!p.startsWith('$')})`
    border-radius: 8px;
    border: 1px solid ${p=>p.theme.palette.primary.main};
`;

const OrderListItem = props=>{
    let {order} = props;
    return <OrderListItemContainer direction="row" gap={2} padding={2}
      justifyContent="space-between" alignItems="center">
        <Typography variant="h5">מנוי {OrderType[order.orderType]}</Typography>
        <Typography>
          התחלה: {formatDateIsrael(order.start)} | סיום: {formatDateIsrael(order.end)}
        </Typography>
    </OrderListItemContainer>
};

const OrderList = props=>{
    let {userId} = props;
    let {data, isLoading} = useOrders(userId);
    if (isLoading)
        return <OrderListSkeleton/>;
    if (!data?.orders.length)
        return <OrderListEmpty/>;
    return <Stack direction="column" gap={2} padding={2}>
      {data.orders.map(order=><OrderListItem key={order.id} userId={userId} order={order}/>)}
    </Stack>;
};

const OrderControls = props=>{
    let {userId} = props;
    let [btnEl, setBtnEl] = useState();
    let [menuOpen, setMenuOpen] = useState();
    let {enqueueSnackbar} = useSnackbar();
    let queryClient = useQueryClient();
    let [create, creating] = useBusyCallback(async type=>{
        setMenuOpen(false);
        try {
            await api.admin.tarotmailOrders.createOrder(userId, type);
            enqueueSnackbar(`נוצר מנוי ${OrderType[type]}`, {variant: 'success'});
            queryClient.invalidateQueries({queryKey: ['tarotmail_orders', userId]});
        } catch (e) {            
            enqueueSnackbar(e.message, {variant: 'error'});
        }
    }, [userId, enqueueSnackbar]);
    return <Stack direction="row" alignItems="center" gap={2}>
      <SCButton ref={setBtnEl} text="יצירת מנוי" variant="contained"
        icon={<AddIcon/>} size="small" loading={creating}
        onClick={()=>setMenuOpen(true)}/>
      <Menu anchorEl={btnEl} open={menuOpen}
        onClose={()=>setMenuOpen(false)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}>
        {_.entries(OrderType).map(([type, label])=><MenuItem
          key={type} onClick={()=>create(type)}>
          <ListItemText>{label}</ListItemText>
        </MenuItem>)}
      </Menu>
    </Stack>;
};

const OrderStatus = props=>{
    let {userId} = props;
    let {data, isLoading} = useOrders(userId);
    let endTime = useMemo(()=>{
        if (!data)
            return null;
        if (!data.endTime)
            return 'אין מנוי בתוקף';
        return 'מנוי בתוקף עד: '+formatDateIsrael(data.endTime);
    }, [data]);
    return isLoading
        ? <Skeleton width={80} height={20}/>
        : <Typography fontWeight="bold">{endTime}</Typography>;
};

const TarotmailOrdersAdmin = props=>{
    let {userId} = props;
    return <Stack direction="column" gap={2}>
      <Stack direction="row" gap={3}
        justifyContent="space-between" alignItems="center">
        <OrderStatus userId={userId}/>
        <OrderControls userId={userId}/>
      </Stack>
      <OrderList userId={userId}/>
    </Stack>;
};

export default TarotmailOrdersAdmin;
